import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { RefundVouchersService } from '../../../services/refund-vouchers.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { BanksService } from '../../../services/banks.service';
import { ParticipantService } from '../../../services/participant.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { CorporateEnrollmentService } from '../../../services/corporate-enrollment.service';
import { CompanyService } from '../../../services/company.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { MaxRefundableFromPlanResponse } from '../../../interfaces/max-refundable-from-plan.interface';

@Component({
  selector: 'app-new-refund',
  templateUrl: './new-refund.component.html',
  styleUrls: ['./new-refund.component.scss']
})
export class NewRefundComponent extends BaseFormComponent implements OnInit {

  // Validator personalizado para el monto máximo
  maxRefundAmountValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // No validar si no hay valor
      }

      const amount = typeof control.value === 'string' ? parseFloat(control.value.replace(/,/g, '')) : control.value;

      // Validación para devolución de servicios
      if (this.selectedRefundType === 'service' && this.maxRefundableAmount > 0 && amount > this.maxRefundableAmount) {
        return {
          maxRefundExceeded: {
            actualAmount: amount,
            maxAmount: this.maxRefundableAmount
          }
        };
      }

      // Validación para devolución de plan - no puede exceder el límite (80% o 100% según derecho al retracto)
      // REFACTORIZADO: Ya no se valida contra la suma de cuotas seleccionadas
      if (this.selectedRefundType === 'plan' && this.planRefundLimits) {
        const useRetract = this.refundForm.get('useRetractRight')?.value || false;
        const maxPlanAmount = useRetract 
          ? this.planRefundLimits.availableToRefundWithRetractRight 
          : this.planRefundLimits.availableToRefund;
        
        if (amount > maxPlanAmount) {
          const percentage = useRetract ? 100 : (this.planRefundLimits.maxRefundPercentage * 100);
          return {
            maxRefundExceeded: {
              actualAmount: amount,
              maxAmount: maxPlanAmount,
              maxPercentage: percentage,
              totalAmount: this.planRefundLimits.totalAmount,
              useRetractRight: useRetract
            }
          };
        }
      }

      return null;
    };
  }
  refundForm: FormGroup;
  banks: any[] = [];
  participants: any[] = [];
  projects: any[] = [];
  selectedParticipant: any = null;
  selectedProject: any = null;
  isLoadingParticipants: boolean = false;
  isLoadingProjects: boolean = false;

  // Corporate enrollment properties
  enrollmentTypes = [
    { value: 'individual', label: 'Inscripción Individual', description: 'Devolución para inscripciones individuales' },
    { value: 'corporate', label: 'Inscripción Corporativa', description: 'Devolución para inscripciones de empresas' }
  ];

  selectedEnrollmentType: string = 'individual';

  // Corporate-specific data
  companies: any[] = [];
  selectedCompany: any = null;
  corporateEnrollments: any[] = [];
  selectedCorporateEnrollment: any = null;
  corporateParticipants: any[] = [];
  selectedCorporateParticipant: any = null;

  // Payer type for corporate enrollments
  payerTypes = [
    { value: 'COMPANY', label: 'Empresa', description: 'La empresa realiza el pago' },
    { value: 'PARTICIPANT_CORPORATE', label: 'Participante Corporativo', description: 'El participante realiza su pago' }
  ];

  selectedPayerType: string = 'COMPANY';

  // Loading states for corporate data
  isLoadingCompanies: boolean = false;
  isLoadingCorporateEnrollments: boolean = false;
  isLoadingCorporateParticipants: boolean = false;

  // Tracking de validación de inscripciones
  participantHasEnrollments: boolean | null = null;
  participantEnrollmentCheckMessage: string = '';
  companyHasEnrollments: boolean | null = null;
  companyEnrollmentCheckMessage: string = '';

  // Opciones de tipo de devolución
  refundTypes = [
    { value: 'excess', label: 'Devolución de Excedente', description: 'Devolver dinero por exceso en pagos' },
    { value: 'plan', label: 'Devolución de Plan de Pagos', description: 'Devolver dinero de cuotas pagadas' },
    { value: 'service', label: 'Devolución de Servicios', description: 'Devolver dinero por otros conceptos' }
  ];

  selectedRefundType: string = '';

  // Datos específicos para cada tipo
  availableExcesses: any[] = [];
  selectedExcess: any = null;
  maxRefundableAmount: number = 0;
  paymentPlans: any[] = [];

  // Para devoluciones de servicios
  availableIncomeReceipts: any[] = [];
  selectedIncomeReceipts: any[] = [];
  selectedIncomeReceiptToAdd: any = null;

  // Para devoluciones de plan - REFACTORIZADO: Ya no se seleccionan cuotas individuales
  // El usuario ingresa un monto libre y el sistema distribuye automáticamente
  planRefundLimits: MaxRefundableFromPlanResponse | null = null;
  isLoadingPlanLimits: boolean = false;

  // 🆕 Moneda de la inscripción para devoluciones de servicios
  serviceRefundCurrencyCode: string = 'COP';

  // 🆕 Control del derecho al retracto
  useRetractRight: boolean = false;

  // Monedas disponibles
  currencies = [
    { value: 'COP', label: 'Peso Colombiano (COP)' },
    { value: 'USD', label: 'Dólar Estadounidense (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' }
  ];

  constructor(
    private fb: FormBuilder,
    private refundVouchersService: RefundVouchersService,
    private banksService: BanksService,
    private participantService: ParticipantService,
    private enrollmentService: EnrollmentService,
    private incomeReceiptsService: IncomeReceiptsService,
    private corporateEnrollmentService: CorporateEnrollmentService,
    private companyService: CompanyService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    router: Router,
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
    this.refundForm = this.fb.group({
      // Campos de tipo de inscripción
      enrollmentType: ['individual', [Validators.required]],
      payerType: ['COMPANY'],

      // Campos para inscripciones individuales
      participantId: [''],
      projectId: [''],

      // Campos para inscripciones corporativas
      companyId: [''],
      corporateEnrollmentId: [''],
      corporateParticipantId: [''],

      // Campos comunes
      refundType: ['', [Validators.required]],
      refundConcept: ['', [Validators.required, Validators.maxLength(200)]],
      bankId: ['', [Validators.required]],
      refundAmount: ['', [Validators.required, Validators.min(0.01), this.maxRefundAmountValidator()]],
      justification: ['', [Validators.required, Validators.maxLength(1000)]],
      observations: ['', [Validators.maxLength(1000)]],
      requestedBy: ['', [Validators.maxLength(100)]],


      // Campos específicos para excedentes
      excessPaymentId: [''],

      // Campos específicos para planes
      enrollmentId: [''],
      // paymentPlanId removido - ahora se usa selectedInstallments array

      // Campos específicos para servicios
      currency: ['COP'],
      exchangeRate: [1],

      // Campo para derecho al retracto
      useRetractRight: [false]
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractDataArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  override ngOnInit(): void {
    super.ngOnInit(); // Llamar al método padre
    this.loadBanks();
    this.loadParticipants();
    this.loadCompanies();

    // Configurar validaciones dinámicas
    this.setupFormValidations();
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    // El formulario ya se inicializa en el constructor
  }

  protected override getMainForm(): FormGroup {
    return this.refundForm;
  }

  public override getFormName(): string {
    const typeLabels = {
      'excess': 'devolución de excedente',
      'plan': 'devolución de plan de pagos',
      'service': 'devolución de servicios'
    };
    return typeLabels[this.selectedRefundType as keyof typeof typeLabels] || 'nueva devolución';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    if (this.selectedEnrollmentType === 'individual') {
      if (this.selectedParticipant) {
        data.push(`Participante: ${this.selectedParticipant.firstName} ${this.selectedParticipant.lastName}`);
      }
    } else if (this.selectedEnrollmentType === 'corporate') {
      if (this.selectedCompany) {
        data.push(`Empresa: ${this.selectedCompany.name}`);
      }
      if (this.selectedCorporateParticipant && this.selectedPayerType === 'PARTICIPANT_CORPORATE') {
        data.push(`Participante: ${this.selectedCorporateParticipant.firstName} ${this.selectedCorporateParticipant.lastName}`);
      }
    }

    const amount = this.refundForm.get('refundAmount')?.value;
    if (amount) {
      data.push(`Monto: ${parseFloat(amount).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}`);
    }

    const concept = this.refundForm.get('refundConcept')?.value;
    if (concept) {
      data.push(`Concepto: ${concept}`);
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    console.log('[NewRefundComponent] performFormSubmit called');
    await this.submitForm();
  }

  protected override navigateBack(): void {
    this.router.navigate(['/refund-vouchers']);
  }

  setupFormValidations(): void {
    // Escuchar cambios en el tipo de inscripción
    this.refundForm.get('enrollmentType')?.valueChanges.subscribe(async (value) => {
      this.selectedEnrollmentType = value;
      await this.resetFormForEnrollmentType(value);
      this.updateValidatorsBasedOnEnrollmentType(value);
      // 🆕 Re-evaluar validadores del tipo de devolución después de cambiar el tipo de inscripción
      if (this.selectedRefundType) {
        this.updateValidatorsBasedOnType(this.selectedRefundType);
      }
    });

    // Escuchar cambios en el tipo de pagador (para inscripciones corporativas)
    this.refundForm.get('payerType')?.valueChanges.subscribe(async (value) => {
      this.selectedPayerType = value;
      await this.resetPayerTypeData();

      // Debug view conditions after payer type changes
      setTimeout(() => this.debugViewConditions(), 100);
    });

    // Escuchar cambios en el tipo de devolución
    this.refundForm.get('refundType')?.valueChanges.subscribe(async (value) => {
      this.selectedRefundType = value;
      await this.resetFormBasedOnType(value);
      this.updateValidatorsBasedOnType(value);

      // Debug view conditions after refund type changes
      setTimeout(() => this.debugViewConditions(), 100);
    });

    // Individual enrollment listeners
    this.refundForm.get('participantId')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && this.selectedEnrollmentType === 'individual') {
          this.loadParticipantDetails(value);
          this.loadStudentProjects(value);
        } else {
          this.resetParticipantData();
        }
      });

    this.refundForm.get('projectId')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && this.selectedParticipant && this.selectedEnrollmentType === 'individual') {
          this.loadDataBasedOnRefundType();
        }
      });

    // Corporate enrollment listeners
    this.refundForm.get('companyId')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && this.selectedEnrollmentType === 'corporate') {
          this.loadCorporateEnrollmentsByCompany(value);
        } else {
          this.resetCompanyData();
        }
      });

    this.refundForm.get('corporateEnrollmentId')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && this.selectedEnrollmentType === 'corporate') {
          this.loadCorporateEnrollmentDetails(value);
        } else {
          this.resetCorporateEnrollmentData();
        }
      });

    this.refundForm.get('corporateParticipantId')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && this.selectedEnrollmentType === 'corporate' && this.selectedPayerType === 'PARTICIPANT_CORPORATE') {
          // Set the selected corporate participant
          const participantId = typeof value === 'string' ? parseInt(value, 10) : value;
          this.selectedCorporateParticipant = this.corporateParticipants.find(p => p.value === participantId || p.participantId === participantId);
          console.log('Selected corporate participant set:', this.selectedCorporateParticipant);

          this.loadCorporateDataBasedOnRefundType();
        } else {
          this.selectedCorporateParticipant = null;
        }
      });

    // Listener para derecho al retracto
    this.refundForm.get('useRetractRight')?.valueChanges.subscribe(value => {
      this.useRetractRight = value;
      // Re-validar el monto cuando cambie el derecho al retracto
      this.updateRefundAmountValidators();
      // Actualizar observaciones automáticamente
      if (value && this.selectedRefundType === 'plan') {
        this.updateObservationsWithRetractRight();
      }
    });
  }

  async resetFormForEnrollmentType(enrollmentType: string): Promise<void> {
    // Confirmar cambio si hay datos que se perderán
    if (this.hasUnsavedChanges()) {
      const fromType = this.selectedEnrollmentType === 'individual' ? 'Inscripción Individual' : 'Inscripción Corporativa';
      const toType = enrollmentType === 'individual' ? 'Inscripción Individual' : 'Inscripción Corporativa';

      const canChange = await this.confirmationService.refund.changeEnrollmentType(fromType, toType);
      if (!canChange) {
        // Revertir la selección
        this.refundForm.patchValue({ enrollmentType: this.selectedEnrollmentType });
        return;
      }
    }

    // Reset all form fields based on enrollment type
    if (enrollmentType === 'individual') {
      this.refundForm.patchValue({
        companyId: '',
        corporateEnrollmentId: '',
        corporateParticipantId: '',
        payerType: ''
      });
      this.resetCorporateData();
    } else if (enrollmentType === 'corporate') {
      this.refundForm.patchValue({
        participantId: '',
        projectId: '',
        payerType: 'COMPANY'
      });
      this.resetParticipantData();
      this.selectedPayerType = 'COMPANY';
    }

    // Reset refund type data
    this.resetFormBasedOnType('');
  }

  updateValidatorsBasedOnEnrollmentType(enrollmentType: string): void {
    // Clear all validators first
    this.refundForm.get('participantId')?.clearValidators();
    this.refundForm.get('projectId')?.clearValidators();
    this.refundForm.get('companyId')?.clearValidators();
    this.refundForm.get('corporateEnrollmentId')?.clearValidators();
    this.refundForm.get('payerType')?.clearValidators();
    this.refundForm.get('corporateParticipantId')?.clearValidators();
    // 🆕 Limpiar enrollmentId también cuando cambia el tipo de inscripción
    this.refundForm.get('enrollmentId')?.clearValidators();

    // Set validators based on enrollment type
    if (enrollmentType === 'individual') {
      this.refundForm.get('participantId')?.setValidators([Validators.required]);
      this.refundForm.get('projectId')?.setValidators([Validators.required]);
      // enrollmentId se validará en updateValidatorsBasedOnType si es necesario (solo para plan)
    } else if (enrollmentType === 'corporate') {
      this.refundForm.get('companyId')?.setValidators([Validators.required]);
      this.refundForm.get('corporateEnrollmentId')?.setValidators([Validators.required]);
      this.refundForm.get('payerType')?.setValidators([Validators.required]);
      // corporateParticipantId se valida dinámicamente según payerType en resetPayerTypeData
      // 🆕 Para corporativas, NO requerimos enrollmentId (se usa corporateEnrollmentId)
    }

    // Update validity
    this.refundForm.get('participantId')?.updateValueAndValidity();
    this.refundForm.get('projectId')?.updateValueAndValidity();
    this.refundForm.get('companyId')?.updateValueAndValidity();
    this.refundForm.get('corporateEnrollmentId')?.updateValueAndValidity();
    this.refundForm.get('payerType')?.updateValueAndValidity();
    this.refundForm.get('corporateParticipantId')?.updateValueAndValidity();
    this.refundForm.get('enrollmentId')?.updateValueAndValidity();
  }

  async resetPayerTypeData(): Promise<void> {
    // Reset participant selection when payer type changes
    if (this.selectedPayerType === 'COMPANY') {
      this.refundForm.patchValue({
        corporateParticipantId: ''
      });
      this.selectedCorporateParticipant = null;
      // Remover validación de corporateParticipantId cuando es COMPANY
      this.refundForm.get('corporateParticipantId')?.clearValidators();
    } else if (this.selectedPayerType === 'PARTICIPANT_CORPORATE') {
      // Agregar validación de corporateParticipantId cuando es PARTICIPANT_CORPORATE
      this.refundForm.get('corporateParticipantId')?.setValidators([Validators.required]);
    }

    // Actualizar validez del campo
    this.refundForm.get('corporateParticipantId')?.updateValueAndValidity();

    // Reset refund-specific data
    await this.resetFormBasedOnType(this.selectedRefundType);
  }

  resetCorporateData(): void {
    this.companies = [];
    this.selectedCompany = null;
    this.corporateEnrollments = [];
    this.selectedCorporateEnrollment = null;
    this.corporateParticipants = [];
    this.selectedCorporateParticipant = null;
    this.companyHasEnrollments = null;
    this.companyEnrollmentCheckMessage = '';
  }

  resetCompanyData(): void {
    this.selectedCompany = null;
    this.corporateEnrollments = [];
    this.selectedCorporateEnrollment = null;
    this.corporateParticipants = [];
    this.selectedCorporateParticipant = null;
    this.companyHasEnrollments = null;
    this.companyEnrollmentCheckMessage = '';
  }

  resetCorporateEnrollmentData(): void {
    this.selectedCorporateEnrollment = null;
    this.corporateParticipants = [];
    this.selectedCorporateParticipant = null;
  }

  async resetFormBasedOnType(refundType: string): Promise<void> {
    // Confirmar cambio si hay selecciones que se perderán
    if (this.selectedRefundType && refundType && this.selectedRefundType !== refundType) {
      const typeLabels = {
        'excess': 'Devolución de Excedente',
        'plan': 'Devolución de Plan de Pagos',
        'service': 'Devolución de Servicios'
      };

      const hasSelections = this.selectedExcess ||
        this.selectedIncomeReceipts.length > 0 ||
        (this.planRefundLimits && this.refundForm.get('refundAmount')?.value > 0);

      if (hasSelections) {
        const fromType = typeLabels[this.selectedRefundType as keyof typeof typeLabels] || this.selectedRefundType;
        const toType = typeLabels[refundType as keyof typeof typeLabels] || refundType;

        const canChange = await this.confirmationService.refund.changeType(fromType, toType);
        if (!canChange) {
          // Revertir la selección
          this.refundForm.patchValue({ refundType: this.selectedRefundType });
          return;
        }
      }
    }

    // Resetear campos específicos
    this.refundForm.patchValue({
      excessPaymentId: '',
      enrollmentId: '',
      paymentPlanId: '',
      currency: refundType === 'service' ? 'COP' : 'COP',
      exchangeRate: 1,
      refundAmount: ''
    });

    // Resetear datos generales
    this.availableExcesses = [];
    this.selectedExcess = null;
    this.paymentPlans = [];
    this.maxRefundableAmount = 0;

    // Resetear datos específicos de servicios
    this.availableIncomeReceipts = [];
    this.selectedIncomeReceipts = [];
    this.selectedIncomeReceiptToAdd = null;

    // Resetear datos específicos de planes - REFACTORIZADO: ya no hay selección de cuotas
    this.planRefundLimits = null;
    this.isLoadingPlanLimits = false;

    // 🆕 Resetear moneda de servicios
    this.serviceRefundCurrencyCode = 'COP';

    // Actualizar validators después del reset
    this.updateRefundAmountValidators();
  }

  updateValidatorsBasedOnType(refundType: string): void {
    // Remover validadores previos
    this.refundForm.get('excessPaymentId')?.clearValidators();
    this.refundForm.get('enrollmentId')?.clearValidators();

    // Agregar validadores según el tipo y el tipo de inscripción
    switch (refundType) {
      case 'excess':
        this.refundForm.get('excessPaymentId')?.setValidators([Validators.required]);
        break;
      case 'plan':
        // 🆕 enrollmentId solo es requerido para inscripciones individuales
        // Para corporativas, se usa corporateEnrollmentId que ya está validado en updateValidatorsBasedOnEnrollmentType
        if (this.selectedEnrollmentType === 'individual') {
          this.refundForm.get('enrollmentId')?.setValidators([Validators.required]);
        }
        // Para corporativas, no requerimos enrollmentId
        break;
      case 'service':
        // No requiere validadores adicionales
        break;
    }

    // Actualizar validadores
    this.refundForm.get('excessPaymentId')?.updateValueAndValidity();
    this.refundForm.get('enrollmentId')?.updateValueAndValidity();
  }

  loadDataBasedOnRefundType(): void {
    const participantId = this.refundForm.get('participantId')?.value;
    const projectId = this.refundForm.get('projectId')?.value;

    if (!participantId || !projectId) return;

    // Asegurar que los IDs sean números
    const parsedParticipantId = typeof participantId === 'string' ? parseInt(participantId, 10) : participantId;
    const parsedProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;

    switch (this.selectedRefundType) {
      case 'excess':
        this.loadAvailableExcesses(parsedParticipantId, parsedProjectId);
        break;
      case 'plan':
        this.loadEnrollmentData(parsedParticipantId, parsedProjectId);
        break;
      case 'service':
        this.loadMaxServiceRefundableAmount(parsedParticipantId, parsedProjectId);
        this.loadAvailableIncomeReceipts(parsedParticipantId, parsedProjectId);
        break;
    }
  }

  // CORPORATE DATA LOADING METHODS

  loadCompanies(): void {
    this.isLoadingCompanies = true;
    this.companyService.getAll().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const companiesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.companies = companiesData.map((company: any) => ({
            value: company.id,
            label: `${company.name} - ${company.documentId}`,
            ...company
          }));
        }
        this.isLoadingCompanies = false;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.toastService.showError('Error al cargar empresas');
        this.isLoadingCompanies = false;
      }
    });
  }

  loadCorporateEnrollmentsByCompany(companyId: number): void {
    this.isLoadingCorporateEnrollments = true;
    const id = typeof companyId === 'string' ? parseInt(companyId, 10) : companyId;

    this.corporateEnrollmentService.getCorporateEnrollmentsByCompany(id).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const corporateEnrollments = this.extractDataArray(response);

          // Validar si la empresa tiene inscripciones corporativas
          if (corporateEnrollments.length === 0) {
            this.companyHasEnrollments = false;
            this.companyEnrollmentCheckMessage = 'La empresa no tiene inscripciones corporativas activas';
            this.toastService.showError(this.companyEnrollmentCheckMessage);
            this.corporateEnrollments = [];
          } else {
            this.companyHasEnrollments = true;
            this.companyEnrollmentCheckMessage = '';

            this.corporateEnrollments = corporateEnrollments.map((enrollment: any) => ({
              value: enrollment.id,
              label: `${enrollment.project?.projectId || 'N/A'} - ${enrollment.project?.name || 'Sin nombre'}`,
              ...enrollment
            }));
          }

          // Update selected company
          this.selectedCompany = this.companies.find(c => c.value === id);
        }
        this.isLoadingCorporateEnrollments = false;
      },
      error: (error) => {
        console.error('Error loading corporate enrollments:', error);
        this.toastService.showError('Error al cargar inscripciones corporativas');
        this.companyHasEnrollments = false;
        this.companyEnrollmentCheckMessage = 'Error al verificar inscripciones corporativas de la empresa';
        this.corporateEnrollments = [];
        this.isLoadingCorporateEnrollments = false;
      }
    });
  }

  loadCorporateEnrollmentDetails(corporateEnrollmentId: number): void {
    this.isLoadingCorporateParticipants = true;
    const id = typeof corporateEnrollmentId === 'string' ? parseInt(corporateEnrollmentId, 10) : corporateEnrollmentId;

    this.corporateEnrollmentService.getAgreementDetails(id).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.selectedCorporateEnrollment = response.data;

          // 🆕 Establecer que la empresa tiene inscripciones (ya que se cargó exitosamente)
          this.companyHasEnrollments = true;
          this.companyEnrollmentCheckMessage = '';

          // 🐛 Debug: Verificar estado después de cargar
          console.log('Corporate enrollment loaded. companyHasEnrollments set to:', this.companyHasEnrollments);
          console.log('Form valid:', this.refundForm.valid);
          console.log('Form errors:', this.getFormErrors());

          // 🆕 Obtener la moneda de la inscripción corporativa
          if (response.data.currencyCode) {
            this.serviceRefundCurrencyCode = response.data.currencyCode;
          } else if (response.data.currency?.code) {
            this.serviceRefundCurrencyCode = response.data.currency.code;
          } else {
            this.serviceRefundCurrencyCode = 'COP'; // Fallback
          }

          // Load participants from the corporate enrollment
          if (response.data.enrollments && response.data.enrollments.length > 0) {
            this.corporateParticipants = response.data.enrollments.map((enrollment: any) => ({
              value: enrollment.participantId,
              label: `${enrollment.participant?.firstName || ''} ${enrollment.participant?.lastName || ''} - ${enrollment.participant?.documentId || ''}`,
              participantId: enrollment.participantId,
              enrollmentId: enrollment.id,
              ...enrollment.participant
            }));
          }

          // Load corporate data based on refund type
          this.loadCorporateDataBasedOnRefundType();

          // Debug view conditions after corporate enrollment is loaded
          setTimeout(() => this.debugViewConditions(), 100);
        } else {
          // Si no se pudo cargar, establecer como false
          this.companyHasEnrollments = false;
          this.companyEnrollmentCheckMessage = 'Error al cargar la inscripción corporativa';
        }
        this.isLoadingCorporateParticipants = false;
      },
      error: (error) => {
        console.error('Error loading corporate enrollment details:', error);
        this.toastService.showError('Error al cargar detalles de inscripción corporativa');
        this.companyHasEnrollments = false;
        this.companyEnrollmentCheckMessage = 'Error al cargar la inscripción corporativa';
        this.isLoadingCorporateParticipants = false;
      }
    });
  }

  loadCorporateDataBasedOnRefundType(): void {
    const corporateEnrollmentId = this.refundForm.get('corporateEnrollmentId')?.value;
    const payerType = this.refundForm.get('payerType')?.value;
    const participantId = this.selectedPayerType === 'PARTICIPANT_CORPORATE' ?
      this.refundForm.get('corporateParticipantId')?.value : null;

    if (!corporateEnrollmentId || !payerType) return;

    // Asegurar que los IDs sean números
    const parsedCorporateEnrollmentId = typeof corporateEnrollmentId === 'string' ? parseInt(corporateEnrollmentId, 10) : corporateEnrollmentId;
    const parsedParticipantId = participantId ? (typeof participantId === 'string' ? parseInt(participantId, 10) : participantId) : null;

    switch (this.selectedRefundType) {
      case 'excess':
        this.loadAvailableCorporateExcesses(parsedCorporateEnrollmentId, payerType, parsedParticipantId);
        break;
      case 'plan':
        this.loadAvailableCorporateInstallments(parsedCorporateEnrollmentId, payerType, parsedParticipantId);
        break;
      case 'service':
        this.loadMaxCorporateRefundableAmount(parsedCorporateEnrollmentId, payerType, parsedParticipantId);
        this.loadAvailableCorporateReceipts(parsedCorporateEnrollmentId, payerType, parsedParticipantId);
        break;
    }
  }

  loadBanks(): void {
    this.banksService.getAll().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          this.banks = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        }
      },
      error: (error) => {
        console.error('Error loading banks:', error);
        this.toastService.showError('Error al cargar bancos');
      }
    });
  }

  loadParticipants(): void {
    this.isLoadingParticipants = true;
    this.participantService.getAll().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const participantsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.participants = participantsData.map((participant: any) => ({
            value: participant.id,
            label: `${participant.firstName} ${participant.lastName} - ${participant.documentId}`,
            ...participant
          }));
        }
        this.isLoadingParticipants = false;
      },
      error: (error) => {
        console.error('Error loading participants:', error);
        this.toastService.showError('Error al cargar participantes');
        this.isLoadingParticipants = false;
      }
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.participantService.getAll().subscribe({
        next: (response) => {
          if (response.isSuccess) {
            const participantsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            this.participants = participantsData.map((participant: any) => ({
              value: participant.id,
              label: `${participant.firstName} ${participant.lastName} - ${participant.documentId}`,
              ...participant
            }));
          }
        },
        error: (err) => console.error('Error loading participants:', err)
      });
      return;
    }

    this.participantService.getAllFiltered(query).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const participantsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.participants = participantsData.map((participant: any) => ({
            value: participant.id,
            label: `${participant.firstName} ${participant.lastName} - ${participant.documentId}`,
            ...participant
          }));
        }
      },
      error: (err) => console.error('Error searching participants:', err)
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.companyService.getAll().subscribe({
        next: (response) => {
          if (response.isSuccess) {
            const companiesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            this.companies = companiesData.map((company: any) => ({
              value: company.id,
              label: `${company.name} - ${company.documentId}`,
              ...company
            }));
          }
        },
        error: (err) => console.error('Error loading companies:', err)
      });
      return;
    }

    this.companyService.getAllFiltered(query).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const companiesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.companies = companiesData.map((company: any) => ({
            value: company.id,
            label: `${company.name} - ${company.documentId}`,
            ...company
          }));
        }
      },
      error: (err) => console.error('Error searching companies:', err)
    });
  }

  loadParticipantDetails(participantId: number): void {
    // Asegurar que el participantId sea un número
    const id = typeof participantId === 'string' ? parseInt(participantId, 10) : participantId;

    this.participantService.getById(id).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.selectedParticipant = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading participant details:', error);
      }
    });
  }

  loadStudentProjects(studentId: number): void {
    this.isLoadingProjects = true;
    // Asegurar que el studentId sea un número
    const id = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;

    this.enrollmentService.getEnrollmentsByStudentId(id).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const enrollments = this.extractDataArray(response);

          // Validar si el participante tiene inscripciones
          if (enrollments.length === 0) {
            this.participantHasEnrollments = false;
            this.participantEnrollmentCheckMessage = 'El estudiante no tiene inscripciones activas, retiradas, aplazadas o canceladas';
            this.toastService.showError(this.participantEnrollmentCheckMessage);
            this.projects = [];
          } else {
            this.participantHasEnrollments = true;
            this.participantEnrollmentCheckMessage = '';

            this.projects = enrollments.map((enrollment: any) => ({
              value: enrollment.project.id,
              label: `${enrollment.project.projectId} - ${enrollment.project.name}`,
              enrollmentId: enrollment.id,
              ...enrollment.project
            }));
          }
        }
        this.isLoadingProjects = false;
      },
      error: (error) => {
        console.error('Error loading student projects:', error);
        this.toastService.showError('Error al cargar proyectos del estudiante');
        this.participantHasEnrollments = false;
        this.participantEnrollmentCheckMessage = 'Error al verificar inscripciones del estudiante';
        this.projects = [];
        this.isLoadingProjects = false;
      }
    });
  }

  resetParticipantData(): void {
    this.selectedParticipant = null;
    this.projects = [];
    this.selectedProject = null;
    this.maxRefundableAmount = 0;
    this.participantHasEnrollments = null;
    this.participantEnrollmentCheckMessage = '';
    this.refundForm.get('projectId')?.setValue('');
  }

  async onSubmit(): Promise<void> {
    await this.handleFormSubmit();
  }

  private async submitForm(): Promise<void> {
    console.log('[NewRefundComponent] submitForm called, form valid:', this.refundForm.valid);
    if (this.refundForm.valid) {
      const formData = this.refundForm.value;

      // Verificar si es un monto elevado y pedir confirmación adicional
      const amount = parseFloat(formData.refundAmount?.toString().replace(/,/g, '') || '0');
      if (amount > 1000000) { // Más de 1 millón
        const confirmLargeAmount = await this.confirmationService.refund.largeAmount(amount);
        if (!confirmLargeAmount) {
          return;
        }
      }

      // Route to appropriate method based on enrollment type
      if (this.selectedEnrollmentType === 'individual') {
        switch (this.selectedRefundType) {
          case 'excess':
            this.createExcessRefund(formData);
            break;
          case 'plan':
            this.createPlanRefund(formData);
            break;
          case 'service':
            this.createServiceRefund(formData);
            break;
        }
      } else if (this.selectedEnrollmentType === 'corporate') {
        switch (this.selectedRefundType) {
          case 'excess':
            this.createCorporateExcessRefund(formData);
            break;
          case 'plan':
            this.createCorporatePlanRefund(formData);
            break;
          case 'service':
            this.createCorporateServiceRefund(formData);
            break;
        }
      }
    } else {
      this.markFormGroupTouched(this.refundForm);
      this.toastService.showError('Por favor, complete todos los campos requeridos');
    }
  }

  createExcessRefund(formData: any): void {
    const payload = {
      excessPaymentId: formData.excessPaymentId,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      justification: formData.justification,
      observations: formData.observations,
      requestedBy: formData.requestedBy,

    };

    this.loadingService.show();
    this.refundVouchersService.createFromExcess(payload).subscribe({
      next: (response) => {
        // Verificar si el response tiene la estructura estándar o es directamente el objeto
        if (response.isSuccess !== undefined) {
          // Formato estándar con isSuccess
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución de excedente creada exitosamente');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución');
          }
        } else if (response.id) {
          // Response directo con el objeto refund voucher creado
          this.toastService.showSuccess('Devolución de excedente creada exitosamente');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating excess refund:', error);
        this.toastService.showError('Error al crear la devolución de excedente');
        this.loadingService.hide();
      }
    });
  }

  createPlanRefund(formData: any): void {
    // REFACTORIZADO: Ya no se seleccionan cuotas, se envía el monto y el sistema distribuye automáticamente
    // Validar que se tenga un monto válido
    if (!formData.refundAmount || formData.refundAmount <= 0) {
      this.toastService.showError('Debe ingresar un monto válido para devolver');
      return;
    }

    // Validar que el monto no exceda el límite permitido (80% o 100% según derecho al retracto)
    if (this.planRefundLimits) {
      const useRetract = formData.useRetractRight || false;
      const maxAllowed = useRetract
        ? this.planRefundLimits.availableToRefundWithRetractRight
        : this.planRefundLimits.availableToRefund;
      if (formData.refundAmount > maxAllowed) {
        this.toastService.showError(`El monto excede el máximo permitido (${maxAllowed.toLocaleString('es-CO', { style: 'currency', currency: this.planRefundLimits.currencyCode || 'COP' })})`);
        return;
      }
    }

    const payload = {
      enrollmentId: formData.enrollmentId,
      // REMOVIDO: paymentPlanIds - Ya no se envían IDs de cuotas
      refundConcept: formData.refundConcept,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      justification: formData.justification,
      observations: formData.observations,
      requestedBy: formData.requestedBy,
      useRetractRight: formData.useRetractRight || false,
    };

    this.loadingService.show();
    this.refundVouchersService.createFromPlan(payload).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución de plan creada exitosamente. El sistema distribuyó el monto automáticamente entre las cuotas afectadas.');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución');
          }
        } else if (response.id) {
          this.toastService.showSuccess('Devolución de plan creada exitosamente. El sistema distribuyó el monto automáticamente entre las cuotas afectadas.');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating plan refund:', error);
        this.toastService.showError('Error al crear la devolución de plan');
        this.loadingService.hide();
      }
    });
  }

  createServiceRefund(formData: any): void {
    // Validar que se haya seleccionado al menos un comprobante de ingreso
    if (this.selectedIncomeReceipts.length === 0) {
      this.toastService.showError('Debe seleccionar al menos un comprobante de ingreso para la devolución');
      return;
    }

    const payload = {
      participantId: typeof formData.participantId === 'string' ? parseInt(formData.participantId, 10) : formData.participantId,
      projectId: typeof formData.projectId === 'string' ? parseInt(formData.projectId, 10) : formData.projectId,
      refundConcept: formData.refundConcept,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      justification: formData.justification,
      observations: this.buildObservationsWithIncomeReceipts(formData.observations),
      requestedBy: formData.requestedBy,
      currency: formData.currency,
      exchangeRate: formData.exchangeRate,
      // Mapear el primer comprobante de ingreso como documento origen
      originDocumentId: this.selectedIncomeReceipts[0].id,
      originDocumentType: 'IncomeReceipt',
      originDocumentReference: this.selectedIncomeReceipts[0].documentNumber || this.selectedIncomeReceipts[0].referenceNumber
    };

    this.loadingService.show();
    this.refundVouchersService.createFromService(payload).subscribe({
      next: (response) => {
        // Verificar si el response tiene la estructura estándar o es directamente el objeto
        if (response.isSuccess !== undefined) {
          // Formato estándar con isSuccess
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución de servicios creada exitosamente');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución');
          }
        } else if (response.id) {
          // Response directo con el objeto refund voucher creado
          this.toastService.showSuccess('Devolución de servicios creada exitosamente');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating service refund:', error);
        this.toastService.showError('Error al crear la devolución de servicios');
        this.loadingService.hide();
      }
    });
  }

  // CORPORATE REFUND CREATION METHODS

  createCorporateExcessRefund(formData: any): void {
    if (!this.selectedExcess) {
      this.toastService.showError('Debe seleccionar un excedente corporativo');
      return;
    }

    const payload = {
      excessPaymentId: formData.excessPaymentId,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      justification: formData.justification,
      corporateEnrollmentId: typeof formData.corporateEnrollmentId === 'string' ? parseInt(formData.corporateEnrollmentId, 10) : formData.corporateEnrollmentId,
      payerType: formData.payerType,
      participantId: this.selectedPayerType === 'PARTICIPANT_CORPORATE' ? (typeof formData.corporateParticipantId === 'string' ? parseInt(formData.corporateParticipantId, 10) : formData.corporateParticipantId) : null,
      observations: formData.observations,
      createdBy: formData.requestedBy
    };

    this.loadingService.show();
    this.refundVouchersService.createFromCorporateExcess(payload).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución corporativa de excedente creada exitosamente');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución corporativa');
          }
        } else if (response.id) {
          this.toastService.showSuccess('Devolución corporativa de excedente creada exitosamente');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución corporativa');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating corporate excess refund:', error);
        this.toastService.showError('Error al crear la devolución corporativa de excedente');
        this.loadingService.hide();
      }
    });
  }

  createCorporatePlanRefund(formData: any): void {
    // REFACTORIZADO: Ya no se seleccionan cuotas, se envía el monto y el sistema distribuye automáticamente
    if (!formData.refundAmount || formData.refundAmount <= 0) {
      this.toastService.showError('Debe ingresar un monto válido para devolver');
      return;
    }

    // Validar que el monto no exceda el límite permitido (80% o 100% según derecho al retracto)
    if (this.planRefundLimits) {
      const useRetract = formData.useRetractRight || false;
      const maxAllowed = useRetract
        ? this.planRefundLimits.availableToRefundWithRetractRight
        : this.planRefundLimits.availableToRefund;
      if (formData.refundAmount > maxAllowed) {
        this.toastService.showError(`El monto excede el máximo permitido (${maxAllowed.toLocaleString('es-CO', { style: 'currency', currency: this.planRefundLimits.currencyCode || 'COP' })})`);
        return;
      }
    }

    const payload = {
      corporateEnrollmentId: typeof formData.corporateEnrollmentId === 'string' ? parseInt(formData.corporateEnrollmentId, 10) : formData.corporateEnrollmentId,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      refundConcept: formData.refundConcept,
      justification: formData.justification,
      payerType: formData.payerType,
      participantId: this.selectedPayerType === 'PARTICIPANT_CORPORATE' ? (typeof formData.corporateParticipantId === 'string' ? parseInt(formData.corporateParticipantId, 10) : formData.corporateParticipantId) : null,
      observations: formData.observations,
      requestedBy: formData.requestedBy,
      useRetractRight: formData.useRetractRight || false
    };

    this.loadingService.show();
    this.refundVouchersService.createFromCorporatePlan(payload).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución corporativa de plan creada exitosamente. El sistema distribuyó el monto automáticamente entre las cuotas afectadas.');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución corporativa');
          }
        } else if (response.id) {
          this.toastService.showSuccess('Devolución corporativa de plan creada exitosamente. El sistema distribuyó el monto automáticamente entre las cuotas afectadas.');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución corporativa');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating corporate plan refund:', error);
        this.toastService.showError('Error al crear la devolución corporativa de plan');
        this.loadingService.hide();
      }
    });
  }

  createCorporateServiceRefund(formData: any): void {
    if (this.selectedIncomeReceipts.length === 0) {
      this.toastService.showError('Debe seleccionar al menos un comprobante corporativo para la devolución');
      return;
    }

    const payload = {
      corporateEnrollmentId: typeof formData.corporateEnrollmentId === 'string' ? parseInt(formData.corporateEnrollmentId, 10) : formData.corporateEnrollmentId,
      bankId: formData.bankId,
      refundAmount: formData.refundAmount,
      justification: formData.justification,
      payerType: formData.payerType,
      participantId: this.selectedPayerType === 'PARTICIPANT_CORPORATE' ? (typeof formData.corporateParticipantId === 'string' ? parseInt(formData.corporateParticipantId, 10) : formData.corporateParticipantId) : null,
      concept: formData.refundConcept,
      observations: formData.observations,
      createdBy: formData.requestedBy
    };

    this.loadingService.show();
    this.refundVouchersService.createFromCorporateService(payload).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.toastService.showSuccess('Devolución corporativa de servicios creada exitosamente');
            this.router.navigate(['/refund-vouchers']);
          } else {
            this.toastService.showError(response.errorMessage || 'Error al crear la devolución corporativa');
          }
        } else if (response.id) {
          this.toastService.showSuccess('Devolución corporativa de servicios creada exitosamente');
          this.router.navigate(['/refund-vouchers']);
        } else {
          this.toastService.showError('Error al crear la devolución corporativa');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error creating corporate service refund:', error);
        this.toastService.showError('Error al crear la devolución corporativa de servicios');
        this.loadingService.hide();
      }
    });
  }

  get isExcessRefund(): boolean {
    return this.selectedRefundType === 'excess';
  }

  get isPlanRefund(): boolean {
    return this.selectedRefundType === 'plan';
  }

  get isServiceRefund(): boolean {
    return this.selectedRefundType === 'service';
  }

  get isRetractRightActive(): boolean {
    return this.refundForm.get('useRetractRight')?.value || false;
  }

  get isFormValidWithEnrollments(): boolean {
    const isBasicFormValid = this.refundForm.valid;

    // 🐛 Debug logging
    console.log('=== isFormValidWithEnrollments DEBUG ===');
    console.log('isBasicFormValid (form.valid):', isBasicFormValid);
    console.log('selectedEnrollmentType:', this.selectedEnrollmentType);
    console.log('companyHasEnrollments:', this.companyHasEnrollments);
    console.log('participantHasEnrollments:', this.participantHasEnrollments);
    console.log('Form errors:', this.getFormErrors());

    if (this.selectedEnrollmentType === 'individual') {
      // Para inscripciones individuales: validar que el participante tenga inscripciones
      const result = isBasicFormValid && this.participantHasEnrollments === true;
      console.log('Individual result:', result);
      return result;
    } else if (this.selectedEnrollmentType === 'corporate') {
      // Para inscripciones corporativas: validar que la empresa tenga inscripciones
      const result = isBasicFormValid && this.companyHasEnrollments === true;
      console.log('Corporate result:', result);
      console.log('isBasicFormValid:', isBasicFormValid, 'companyHasEnrollments:', this.companyHasEnrollments);
      return result;
    }

    console.log('Default result:', isBasicFormValid);
    return isBasicFormValid;
  }

  // 🐛 Helper para obtener errores del formulario
  private getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.refundForm.controls).forEach(key => {
      const control = this.refundForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  // Debug helper method
  debugViewConditions(): void {
    console.log('=== DEBUG VIEW CONDITIONS ===');
    console.log('isExcessRefund:', this.isExcessRefund);
    console.log('selectedEnrollmentType:', this.selectedEnrollmentType);
    console.log('selectedRefundType:', this.selectedRefundType);
    console.log('selectedCorporateEnrollment:', this.selectedCorporateEnrollment);
    console.log('selectedPayerType:', this.selectedPayerType);
    console.log('selectedCorporateParticipant:', this.selectedCorporateParticipant);
    console.log('availableExcesses:', this.availableExcesses);
    console.log('shouldShowExcessSection:',
      this.isExcessRefund &&
      this.selectedEnrollmentType === 'corporate' &&
      this.selectedCorporateEnrollment &&
      ((this.selectedPayerType === 'COMPANY') ||
        (this.selectedPayerType === 'PARTICIPANT_CORPORATE' && this.selectedCorporateParticipant))
    );
    console.log('==============================');
  }

  // CORPORATE-SPECIFIC DATA LOADING METHODS

  loadAvailableCorporateExcesses(corporateEnrollmentId: number, payerType: string, participantId?: number): void {
    console.log('Loading corporate excesses for:', { corporateEnrollmentId, payerType, participantId });

    this.refundVouchersService.getAvailableCorporateExcesses(corporateEnrollmentId, payerType, participantId).subscribe({
      next: (response) => {
        console.log('Corporate excesses response:', response);

        // Handle both response formats: direct array or wrapper object
        let excessesData: any[] = [];

        if (Array.isArray(response)) {
          // Direct array response
          excessesData = response;
        } else if (response && response.isSuccess && Array.isArray(response.data)) {
          // Standard wrapper response
          excessesData = response.data;
        } else if (response && Array.isArray(response.data)) {
          // Wrapper response without isSuccess check
          excessesData = response.data;
        } else {
          console.warn('Unexpected response format for corporate excesses:', response);
          this.toastService.showError('Formato de respuesta inesperado para excedentes corporativos');
          return;
        }

        if (excessesData.length > 0) {
          this.availableExcesses = excessesData.map((excess: any) => ({
            ...excess,
            displayLabel: `Excedente #${excess.id} - $${excess.excessAmount?.toLocaleString() || '0'} - ${excess.concept || 'Sin concepto'}`
          }));
          console.log('Available excesses set:', this.availableExcesses);
        } else {
          this.availableExcesses = [];
          console.log('No corporate excesses available');
        }
      },
      error: (error) => {
        console.error('Error loading corporate excesses:', error);
        this.toastService.showError('Error al cargar excedentes corporativos');
      }
    });
  }

  loadAvailableCorporateInstallments(corporateEnrollmentId: number, payerType: string, participantId?: number): void {
    console.log('Loading corporate plan refund limits for:', { corporateEnrollmentId, payerType, participantId });

    // REFACTORIZADO: Ya no cargamos cuotas individuales, cargamos los límites de devolución
    this.loadCorporatePlanRefundLimits(corporateEnrollmentId, payerType, participantId ?? undefined);
  }

  loadAvailableCorporateReceipts(corporateEnrollmentId: number, payerType: string, participantId?: number): void {
    console.log('Loading corporate receipts for:', { corporateEnrollmentId, payerType, participantId });

    this.refundVouchersService.getAvailableCorporateReceipts(corporateEnrollmentId, payerType, participantId).subscribe({
      next: (response) => {
        console.log('Corporate receipts response:', response);

        // Handle both response formats: direct array or wrapper object
        let receiptsData: any[] = [];

        if (Array.isArray(response)) {
          // Direct array response
          receiptsData = response;
        } else if (response && response.isSuccess && Array.isArray(response.data)) {
          // Standard wrapper response
          receiptsData = response.data;
        } else if (response && Array.isArray(response.data)) {
          // Wrapper response without isSuccess check
          receiptsData = response.data;
        } else {
          console.warn('Unexpected response format for corporate receipts:', response);
          this.toastService.showError('Formato de respuesta inesperado para comprobantes corporativos');
          return;
        }

        if (receiptsData.length >= 0) {
          this.availableIncomeReceipts = receiptsData
            .filter((receipt: any) => !this.selectedIncomeReceipts.some(selected => selected.id === receipt.id))
            .map((receipt: any) => ({
              ...receipt,
              displayLabel: `${receipt.documentNumber} - $${receipt.grossAmount?.toLocaleString() || '0'} - ${receipt.concept || 'Sin concepto'}`
            }));
          console.log('Available receipts set:', this.availableIncomeReceipts);
        } else {
          this.availableIncomeReceipts = [];
          console.log('No corporate receipts available');
        }
      },
      error: (error) => {
        console.error('Error loading corporate receipts:', error);
        this.toastService.showError('Error al cargar comprobantes corporativos');
      }
    });
  }

  loadMaxCorporateRefundableAmount(corporateEnrollmentId: number, payerType: string, participantId?: number): void {
    console.log('Loading max corporate refundable amount for:', { corporateEnrollmentId, payerType, participantId });

    this.refundVouchersService.getMaxCorporateRefundableAmount(corporateEnrollmentId, payerType, participantId).subscribe({
      next: (response) => {
        console.log('Max corporate refundable amount response:', response);

        // Handle both response formats: direct value or wrapper object
        let maxAmount: number = 0;

        if (typeof response === 'number') {
          // Direct number response
          maxAmount = response;
        } else if (response && response.isSuccess && typeof response.data === 'number') {
          // Standard wrapper response
          maxAmount = response.data;
        } else if (response && typeof response.data === 'number') {
          // Wrapper response without isSuccess check
          maxAmount = response.data;
        } else {
          console.warn('Unexpected response format for max corporate refundable amount:', response);
          this.toastService.showError('Formato de respuesta inesperado para monto máximo corporativo');
          return;
        }

        this.maxRefundableAmount = maxAmount;
        this.updateRefundAmountValidators();
        console.log('Max corporate refundable amount set:', this.maxRefundableAmount);
      },
      error: (error) => {
        console.error('Error loading max corporate refundable amount:', error);
        this.toastService.showError('Error al calcular monto máximo corporativo');
      }
    });
  }

  // Métodos de carga de datos específicos por tipo
  selectExcess(excess: any): void {
    this.selectedExcess = excess;
    this.refundForm.patchValue({
      excessPaymentId: excess.id,
      refundAmount: excess.excessAmount
    });
    // Actualizar validators después de cambiar el monto automáticamente
    this.updateRefundAmountValidators();
  }

  loadAvailableExcesses(participantId: number, projectId: number): void {
    this.refundVouchersService.getAvailableExcessesByParticipantAndProject(participantId, projectId).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const excessesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.availableExcesses = excessesData.map((excess: any) => ({
            ...excess,
            displayLabel: `Excedente #${excess.id} - ${excess.excessAmount} - ${excess.concept || 'Sin concepto'}`
          }));
        } else {
          this.toastService.showError('Error al cargar excedentes disponibles');
        }
      },
      error: (error) => {
        console.error('Error loading available excesses:', error);
        this.toastService.showError('Error al cargar excedentes disponibles');
      }
    });
  }

  loadEnrollmentData(participantId: number, projectId: number): void {
    // Buscar el enrollment correspondiente al participante y proyecto
    const selectedProjectData = this.projects.find(p => p.value === projectId);

    if (selectedProjectData && selectedProjectData.enrollmentId) {
      this.refundForm.patchValue({ enrollmentId: selectedProjectData.enrollmentId });

      // 🆕 Cargar la información de la inscripción para obtener la moneda
      this.enrollmentService.getById(selectedProjectData.enrollmentId).subscribe({
        next: (response) => {
          if (response.isSuccess && response.data) {
            // Guardar la moneda de la inscripción para usar en servicios
            this.serviceRefundCurrencyCode = response.data.currencyCode || response.data.currency?.code || 'COP';
          }
        },
        error: (err) => {
          console.error('Error loading enrollment for currency:', err);
          // Mantener COP como fallback
          this.serviceRefundCurrencyCode = 'COP';
        }
      });

      // REFACTORIZADO: Cargar los límites de devolución desde el nuevo endpoint
      this.loadPlanRefundLimits(selectedProjectData.enrollmentId);
    }
  }

  /**
   * Carga los límites de devolución para un plan de pagos individual.
   * Incluye: TotalAmount, MaxRefundable (80%), TotalPaid, AlreadyRefunded, AvailableToRefund
   */
  loadPlanRefundLimits(enrollmentId: number): void {
    this.isLoadingPlanLimits = true;
    this.planRefundLimits = null;

    this.refundVouchersService.getMaxRefundableFromPlan(enrollmentId).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.planRefundLimits = response.data;
          } else {
            this.toastService.showError(response.errorMessage || 'Error al cargar límites de devolución');
          }
        } else {
          // Respuesta directa sin wrapper
          this.planRefundLimits = response;
        }

        if (this.planRefundLimits) {
          this.maxRefundableAmount = this.planRefundLimits.availableToRefund;
          this.updateRefundAmountValidators();

          console.log('Plan refund limits loaded:', this.planRefundLimits);

          if (!this.planRefundLimits.canRefund) {
            this.toastService.showWarning(this.planRefundLimits.message || 'No hay monto disponible para devolución');
          }
        }

        this.isLoadingPlanLimits = false;
      },
      error: (error) => {
        console.error('Error loading plan refund limits:', error);
        this.toastService.showError('Error al cargar límites de devolución del plan');
        this.isLoadingPlanLimits = false;
      }
    });
  }

  /**
   * Carga los límites de devolución para un plan de pagos corporativo.
   */
  loadCorporatePlanRefundLimits(corporateEnrollmentId: number, payerType: string, participantId?: number): void {
    this.isLoadingPlanLimits = true;
    this.planRefundLimits = null;

    this.refundVouchersService.getMaxRefundableFromCorporatePlan(corporateEnrollmentId, payerType, participantId).subscribe({
      next: (response) => {
        if (response.isSuccess !== undefined) {
          if (response.isSuccess) {
            this.planRefundLimits = response.data;
          } else {
            this.toastService.showError(response.errorMessage || 'Error al cargar límites de devolución corporativos');
          }
        } else {
          this.planRefundLimits = response;
        }

        if (this.planRefundLimits) {
          this.maxRefundableAmount = this.planRefundLimits.availableToRefund;
          this.updateRefundAmountValidators();

          console.log('Corporate plan refund limits loaded:', this.planRefundLimits);

          if (!this.planRefundLimits.canRefund) {
            this.toastService.showWarning(this.planRefundLimits.message || 'No hay monto disponible para devolución');
          }
        }

        this.isLoadingPlanLimits = false;
      },
      error: (error) => {
        console.error('Error loading corporate plan refund limits:', error);
        this.toastService.showError('Error al cargar límites de devolución corporativos');
        this.isLoadingPlanLimits = false;
      }
    });
  }

  loadMaxServiceRefundableAmount(participantId: number, projectId: number): void {
    this.refundVouchersService.getMaxServiceRefundableAmount(participantId, projectId).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.maxRefundableAmount = response.data;
          // Actualizar validators del monto cuando cambie el máximo
          this.updateRefundAmountValidators();
        } else {
          this.toastService.showError('Error al obtener monto máximo reembolsable');
        }
      },
      error: (error) => {
        console.error('Error loading max refundable amount:', error);
        this.toastService.showError('Error al calcular monto máximo');
      }
    });
  }

  updateRefundAmountValidators(): void {
    const refundAmountControl = this.refundForm.get('refundAmount');
    if (refundAmountControl) {
      refundAmountControl.setValidators([
        Validators.required,
        Validators.min(0.01),
        this.maxRefundAmountValidator()
      ]);
      refundAmountControl.updateValueAndValidity();
    }
  }

  // Métodos para devoluciones de servicios (comprobantes)
  loadAvailableIncomeReceipts(participantId: number, projectId: number): void {
    // Resetear selección previa
    this.selectedIncomeReceiptToAdd = null;

    // Usar el endpoint correcto según el tipo de devolución
    let observable;
    if (this.selectedRefundType === 'service') {
      // Para servicios: comprobantes NO relacionados a planes de pago
      observable = this.incomeReceiptsService.getByParticipantAndProject(participantId, projectId);
    } else if (this.selectedRefundType === 'plan') {
      // Para planes: comprobantes SÍ relacionados a planes de pago
      observable = this.incomeReceiptsService.getPlanRelatedByParticipantAndProject(participantId, projectId);
    } else {
      return; // No cargar comprobantes para excedentes
    }

    observable.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.availableIncomeReceipts = response.data
            .filter((receipt: any) => !this.selectedIncomeReceipts.some(selected => selected.id === receipt.id))
            .map((receipt: any) => ({
              ...receipt,
              displayLabel: `${receipt.documentNumber} - $${receipt.grossAmount?.toLocaleString() || '0'} - ${receipt.concept || 'Sin concepto'}`
            }));


        } else {
          this.toastService.showError('Error al cargar comprobantes disponibles');
        }
      },
      error: (error) => {
        console.error('Error loading receipts:', error);
        this.toastService.showError('Error al cargar comprobantes');
      }
    });
  }

  addSelectedIncomeReceipt(receiptId: string): void {
    if (!receiptId) return;

    const receipt = this.availableIncomeReceipts.find(r => r.id.toString() === receiptId);
    if (receipt) {
      this.selectedIncomeReceipts.push(receipt);
      this.availableIncomeReceipts = this.availableIncomeReceipts.filter(r => r.id.toString() !== receiptId);
      this.updateRefundAmountFromReceipts();
    }
  }

  addSelectedIncomeReceiptFromDropdown(): void {
    if (!this.selectedIncomeReceiptToAdd) return;

    // Buscar con conversión de tipos si es necesario
    const receipt = this.availableIncomeReceipts.find(r =>
      r.id === this.selectedIncomeReceiptToAdd ||
      r.id.toString() === this.selectedIncomeReceiptToAdd.toString()
    );

    if (receipt) {
      this.selectedIncomeReceipts.push(receipt);
      this.availableIncomeReceipts = this.availableIncomeReceipts.filter(r =>
        r.id !== this.selectedIncomeReceiptToAdd &&
        r.id.toString() !== this.selectedIncomeReceiptToAdd.toString()
      );
      this.selectedIncomeReceiptToAdd = null;
      this.updateRefundAmountFromReceipts();
    }
  }

  getSelectedReceiptForDisplay(): any {
    if (!this.selectedIncomeReceiptToAdd) {
      return null;
    }

    // Intentar buscar con conversión de tipos más robusta
    const found = this.availableIncomeReceipts.find(r => {
      // Convertir ambos valores a string para comparación
      const receiptId = String(r.id);
      const selectedId = String(this.selectedIncomeReceiptToAdd);
      return receiptId === selectedId;
    });

    // Si no se encuentra, intentar con comparación directa
    if (!found) {
      return this.availableIncomeReceipts.find(r => r.id === this.selectedIncomeReceiptToAdd);
    }

    return found;
  }

  onIncomeReceiptSelect(event: any): void {
    // Evento cuando se selecciona un comprobante
    if (event && event.value) {
      this.selectedIncomeReceiptToAdd = event.value;
    }
  }

  async removeIncomeReceipt(index: number): Promise<void> {
    if (index >= 0 && index < this.selectedIncomeReceipts.length) {
      const receipt = this.selectedIncomeReceipts[index];
      const receiptDescription = `${receipt.documentNumber} - ${receipt.concept || 'Sin concepto'}`;

      const canRemove = await this.confirmDelete('comprobante de ingreso', receiptDescription);
      if (!canRemove) {
        return;
      }

      const removedReceipt = this.selectedIncomeReceipts.splice(index, 1)[0];
      if (removedReceipt) {
        // Agregar el displayLabel al receipt removido antes de agregarlo de vuelta
        const receiptWithLabel = {
          ...removedReceipt,
          displayLabel: `${removedReceipt.documentNumber} - ${removedReceipt.grossAmount} - ${removedReceipt.concept || 'Sin concepto'}`
        };
        this.availableIncomeReceipts.push(receiptWithLabel);

        // Resetear la selección del dropdown
        this.selectedIncomeReceiptToAdd = null;
      }
      this.updateRefundAmountFromReceipts();
    }
  }

  getTotalIncomeReceipts(): number {
    return this.selectedIncomeReceipts.reduce((total, receipt) =>
      total + (receipt.grossAmount || 0), 0
    );
  }

  updateRefundAmountFromReceipts(): void {
    const total = this.getTotalIncomeReceipts();
    this.refundForm.patchValue({ refundAmount: total });
    // Actualizar validators después de cambiar el monto automáticamente
    this.updateRefundAmountValidators();
  }

  /**
   * Construye las observaciones como texto plano (sin JSON estructurado)
   */
  buildObservationsWithIncomeReceipts(originalObservations: string = ''): string {
    // Simplemente retornar las observaciones originales
    // La información de comprobantes se maneja ahora a través de DocumentId y DocumentType
    return originalObservations || '';
  }

  /**
   * Actualiza las observaciones cuando se activa/desactiva el derecho al retracto
   */
  updateObservationsWithRetractRight(): void {
    const currentObservations = this.refundForm.get('observations')?.value || '';
    const retractRightText = 'Derecho al retracto aplicado - Devolución del 100%';
    
    if (this.useRetractRight) {
      // Agregar el texto de derecho al retracto si no está presente
      if (!currentObservations.includes(retractRightText)) {
        const newObservations = currentObservations 
          ? `${currentObservations}\n\n${retractRightText}` 
          : retractRightText;
        this.refundForm.patchValue({ observations: newObservations });
      }
    } else {
      // Remover el texto de derecho al retracto si está presente
      const updatedObservations = currentObservations
        .replace(new RegExp(`\\n\\n${retractRightText}|${retractRightText}\\n\\n?`, 'g'), '')
        .replace(new RegExp(`${retractRightText}`, 'g'), '')
        .trim();
      this.refundForm.patchValue({ observations: updatedObservations });
    }
  }

  // === MÉTODOS DE CANCELACIÓN Y NAVEGACIÓN ===

  /**
   * Maneja el botón cancelar con confirmación
   */
  async onCancel(): Promise<void> {
    await this.handleFormCancel('/refund-vouchers');
  }

  /**
   * Maneja la confirmación del modal
   */
  onConfirmationModalConfirmed(): void {
    console.log('[NewRefundComponent] onConfirmationModalConfirmed called');
    this.onModalConfirmed();
  }

  /**
   * Maneja la cancelación del modal
   */
  onConfirmationModalCancelled(): void {
    this.onModalCancelled();
  }

  /**
   * Maneja el cierre del modal
   */
  onConfirmationModalClosed(): void {
    this.onModalClosed();
  }
}